import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ExternalLink,
    Hammer,
    Loader2,
    Plus,
    RefreshCw,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { icraMerkez } from "../lib/icraApi";

function formatTl(val) {
    if (val == null || val === "") return "—";
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 2,
    }).format(n);
}

export default function IcraDosyaDetailSection({ row, onRefresh }) {
    const [odemeler, setOdemeler] = useState([]);
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tutar, setTutar] = useState("");
    const [anlasma, setAnlasma] = useState("");
    const [busy, setBusy] = useState(false);
    const sdId = row?.sigorta_dosyasi || row?.sigorta_dosyasi_id;

    const loadOdemeler = useCallback(async () => {
        if (!row?.id) return;
        setLoading(true);
        try {
            const data = await icraMerkez.odemeler(row.id);
            setOdemeler(data.odemeler || []);
            setOzet(data);
        } catch {
            setOdemeler([]);
            setOzet(null);
        } finally {
            setLoading(false);
        }
    }, [row?.id]);

    useEffect(() => {
        loadOdemeler();
    }, [loadOdemeler]);

    const tahsilatEkle = async () => {
        const miktar = Number(String(tutar).replace(",", "."));
        if (!miktar || miktar <= 0) {
            toast.error("Geçerli tutar girin");
            return;
        }
        setBusy(true);
        try {
            await icraMerkez.tahsilatKaydi(row.id, {
                odeme_miktari: miktar,
                anlasma_orani: anlasma || 0,
                odeme_yapildi: true,
            });
            toast.success("İcra tahsilatı kaydedildi — ödeme takibine eklendi");
            setTutar("");
            loadOdemeler();
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kayıt başarısız");
        } finally {
            setBusy(false);
        }
    };

    const kalan = ozet?.kalan_borc ?? row?.kalan_borc;
    const tahsil = ozet?.tahsil_edilen ?? row?.tahsil_edilen_tutar;

    return (
        <div className="space-y-6 mb-8">
            <section className="rounded-xl border border-orange-500/30 bg-gradient-to-br from-[#121A2F] to-[#0B0F19] p-5">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Hammer className="h-5 w-5 text-orange-300" />
                    <h2 className="font-display text-lg text-white">İcra takibi</h2>
                    {row?.dosya_durumu_display && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded border border-white/10 text-orange-200">
                            {row.dosya_durumu_display}
                        </span>
                    )}
                    {row?.odeme_tahsil_edildi && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                            Tahsil tamam
                        </span>
                    )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">İcra dosya no</div>
                        <div className="font-mono text-[#F3E5AB] mt-1">{row?.icra_dosya_no || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Borç tutarı</div>
                        <div className="text-slate-200 mt-1">{formatTl(row?.borc_tutari)}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Tahsil edilen</div>
                        <div className="text-emerald-300 mt-1">{formatTl(tahsil)}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Kalan borç</div>
                        <div className="text-orange-200 mt-1">{formatTl(kalan)}</div>
                    </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                    <div>
                        <span className="text-slate-500">İcra müdürlüğü: </span>
                        <span className="text-slate-200">{row?.icra_mudurlugu || "—"}</span>
                    </div>
                    <div>
                        <span className="text-slate-500">Net kazanç: </span>
                        <span className="text-[#F3E5AB]">{formatTl(row?.kazanc)}</span>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                    <Link
                        to="/yeni/icra-merkezi"
                        className="text-xs text-orange-300 hover:underline"
                    >
                        İcra Merkezi →
                    </Link>
                    <Link
                        to="/e/icralar"
                        className="text-xs text-[#D4AF37] hover:underline"
                    >
                        İcra listesi →
                    </Link>
                    {sdId && (
                        <Link
                            to={`/e/sigorta-dosyalari/${sdId}`}
                            className="text-xs text-slate-400 hover:underline inline-flex items-center gap-1"
                        >
                            Sigorta dosyası <ExternalLink className="h-3 w-3" />
                        </Link>
                    )}
                </div>
            </section>

            <section className="rounded-xl border border-emerald-500/20 bg-[#121A2F]/80 p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Wallet className="h-4 w-4 text-emerald-300" />
                    <h3 className="text-sm font-semibold text-white">İcra tahsilatları (ödeme takibi)</h3>
                    <button
                        type="button"
                        onClick={loadOdemeler}
                        className="ml-auto p-1 text-slate-500 hover:text-white"
                        title="Yenile"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 items-end mb-4 p-3 rounded-lg bg-[#0B0F19]/80 border border-white/5">
                    <div>
                        <label className="text-[10px] text-slate-500 block mb-1">Tahsilat tutarı (TL)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tutar}
                            onChange={(e) => setTutar(e.target.value)}
                            className="rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-1.5 text-sm text-white w-36"
                            placeholder="0.00"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 block mb-1">Anlaşma %</label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={anlasma}
                            onChange={(e) => setAnlasma(e.target.value)}
                            className="rounded-lg bg-[#0B0F19] border border-white/10 px-2 py-1.5 text-sm text-white w-24"
                            placeholder="0"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={tahsilatEkle}
                        disabled={busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                    >
                        {busy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Plus className="h-3.5 w-3.5" />
                        )}
                        Tahsilat ekle
                    </button>
                    <Link
                        to={`/e/odemeler?icra_takibi=${row?.id}`}
                        className="text-xs text-slate-400 hover:text-[#D4AF37] ml-2"
                    >
                        Tüm ödemelerde filtrele →
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-6">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
                    </div>
                ) : odemeler.length === 0 ? (
                    <p className="text-sm text-slate-500">Henüz icra tahsilat kaydı yok.</p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-white/10">
                        <table className="w-full text-xs">
                            <thead className="bg-[#0B0F19] text-slate-500 uppercase">
                                <tr>
                                    <th className="p-2 text-left">Tarih</th>
                                    <th className="p-2 text-left">Tür</th>
                                    <th className="p-2 text-right">Tutar</th>
                                    <th className="p-2 text-left">Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {odemeler.map((o) => (
                                    <tr key={o.id} className="border-t border-white/5">
                                        <td className="p-2 text-slate-400">
                                            {(o.kayit_tarihi || "").slice(0, 10)}
                                        </td>
                                        <td className="p-2 text-slate-300">
                                            {o.odeme_turu_display || o.odeme_turu}
                                        </td>
                                        <td className="p-2 text-right text-emerald-300 font-mono">
                                            {formatTl(o.odeme_miktari)}
                                        </td>
                                        <td className="p-2">
                                            {o.odeme_yapildi ? (
                                                <span className="text-emerald-400">Ödendi</span>
                                            ) : (
                                                <span className="text-amber-400">Bekliyor</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {row?.panel_notlar && (
                <section className="rounded-xl border border-white/10 bg-[#121A2F] p-4">
                    <h3 className="text-xs uppercase text-slate-500 mb-2">İcra notları</h3>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{row.panel_notlar}</p>
                </section>
            )}
        </div>
    );
}
